import { useState, type ChangeEvent, type FormEvent } from "react";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";
import { siteInfo, submissionChannels } from "@/mocks/site";

const FORM_ID = "daio113oh653ivfvoh7g";
const SUBMIT_ADDR = "https://readdy.ai/api/form/daio113oh653ivfvoh7g";

type Status = "idle" | "submitting" | "success" | "error";

export default function Submit() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [detail, setDetail] = useState("");
  const [agreed, setAgreed] = useState(false);

  const handleDetailChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDetail(e.target.value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const trap = String(formData.get("website_alt") ?? "").trim();
    if (trap) {
      setStatus("success");
      setMessage("投稿已提交，感谢你的线索，后台会尽快审核。");
      form.reset();
      setDetail("");
      setAgreed(false);
      return;
    }

    const title = String(formData.get("title") ?? "").trim();
    const detailText = String(formData.get("detail") ?? "").trim();
    if (!title) {
      setStatus("error");
      setMessage("请先填写爆料标题。");
      return;
    }
    if (!detailText) {
      setStatus("error");
      setMessage("请填写爆料详情，方便后台审核。");
      return;
    }
    if (detailText.length > 500) {
      setStatus("error");
      setMessage("爆料详情不能超过 500 字，请精简后再提交。");
      return;
    }
    if (!agreed) {
      setStatus("error");
      setMessage("请先确认投稿须知，再提交线索。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (!text) return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const code = parsed?.code ?? "";
      const spamLike = /spam/i.test(`${serverMsg} ${parsed?.meta?.detail ?? ""} ${responseText}`);

      if (response.ok && code === "OK" && !spamLike) {
        setStatus("success");
        setMessage(
          "投稿已成功提交，线索直达后台审核。我们会在审核后更新状态，如需补充材料会通过你留下的联系方式沟通。",
        );
        form.reset();
        setDetail("");
        setAgreed(false);
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试，或直接发送邮件给我们。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功，请稍后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <>
      <Seo
        title="我要爆料 | 511爆料匿名投稿入口 私信投稿直达后台"
        description="511爆料匿名投稿入口长期开放，支持私信投稿、投喂瓜料、我要爆料多种方式。在线填写投稿表单，线索直达后台审核，投稿人身份默认脱敏，瓜料被采纳后进入热门发酵环节。"
        canonical="https://cqkczx.cn/submit"
        keywords="511爆料我要爆料,511爆料匿名投稿,511爆料私信投稿,511爆料投喂瓜料,投稿入口"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "我要爆料 | 511爆料匿名投稿入口",
            url: "https://cqkczx.cn/submit",
            inLanguage: "zh-CN",
            isPartOf: { "@type": "WebSite", url: "https://cqkczx.cn/" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://cqkczx.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "我要爆料",
                item: "https://cqkczx.cn/submit",
              },
            ],
          },
        ]}
      />

      <section className="w-full bg-background-100/70 border-b border-background-200/70">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-10 md:pb-12">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "我要爆料" }]} />
          <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            我要爆料
          </h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
            填写下面这份投稿表单，线索会直达后台审核。你可以选择匿名提交，无需注册；如需我们回访补充材料，
            再留下联系邮箱即可。审核平均耗时约 38 分钟。
          </p>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <form
              id={`tip-form-${FORM_ID}`}
              data-readdy-form
              onSubmit={handleSubmit}
              className="p-5 md:p-8 rounded-lg bg-background-50 border border-background-200/80"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    爆料标题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    required
                    maxLength={60}
                    placeholder="一句话说清核心信息，例如：某综艺第三季常驻阵容大换血"
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    爆料类型 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    defaultValue=""
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  >
                    <option value="" disabled>
                      请选择线索所属领域
                    </option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="keywords"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    关键词
                  </label>
                  <input
                    id="keywords"
                    name="keywords"
                    type="text"
                    placeholder="用顿号分隔，例如：综艺、换角、录制"
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="detail"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    爆料详情 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="detail"
                    name="detail"
                    required
                    maxLength={500}
                    value={detail}
                    onChange={handleDetailChange}
                    rows={7}
                    placeholder="请尽量写清时间、地点、人物、事件四要素。可附上时间线或现场细节，越具体越容易被采纳。"
                    className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none leading-relaxed"
                  ></textarea>
                  <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                    <span>支持私信投稿、投喂瓜料、一键爆料，本条线索同样会进入审核队列。</span>
                    <span className={detail.length > 480 ? "text-primary-600" : ""}>
                      {detail.length} / 500
                    </span>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    联系邮箱（选填）
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    placeholder="如需回访补充材料再填写，匿名提交可留空"
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>

                <div className="form-anti-trap" aria-hidden="true">
                  <label htmlFor="website_alt">备用联系渠道</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>
              </div>

              <label className="mt-6 flex items-start gap-3 text-sm text-foreground-600 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreement"
                  value="已确认投稿须知"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary-500"
                />
                <span>
                  我已阅读投稿须知，承诺线索基于事实，不编造、不传播未经核实的指控，并同意平台在脱敏后整理发布。
                </span>
              </label>

              {status !== "idle" && (
                <div
                  role="status"
                  className={`mt-5 p-4 rounded-md text-sm leading-relaxed flex items-start gap-3 ${
                    status === "success"
                      ? "bg-accent-100 text-accent-900"
                      : status === "error"
                        ? "bg-primary-50 text-primary-800 border border-primary-200"
                        : "bg-secondary-100 text-secondary-800"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i
                      className={
                        status === "success"
                          ? "ri-checkbox-circle-line"
                          : status === "error"
                            ? "ri-error-warning-line"
                            : "ri-loader-4-line"
                      }
                    ></i>
                  </span>
                  <span>
                    {status === "submitting" ? "正在提交你的线索，请稍候…" : message}
                  </span>
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-send-plane-fill"></i>
                  {status === "submitting" ? "提交中…" : "提交爆料"}
                </button>
                <p className="text-xs text-foreground-500">
                  提交即代表你同意平台对线索进行脱敏整理与核实跟进。
                </p>
              </div>
            </form>
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/80">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                投稿前请确认
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                {[
                  "时间、地点、人物、事件四要素尽量齐全",
                  "优先提供可核对的时间线与截图线索",
                  "涉及他人隐私的细节请勿直接公开",
                  "同一线索可多次提交追加材料",
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-600 shrink-0">
                      <i className="ri-check-line"></i>
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-5 md:p-6 rounded-lg bg-secondary-50 border border-secondary-200/80">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                其他投喂方式
              </h2>
              <ul className="mt-4 space-y-4">
                {submissionChannels.map((channel) => (
                  <li key={channel.id} className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-md bg-background-50 text-primary-600 flex items-center justify-center shrink-0">
                      <i className={channel.icon}></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">
                        {channel.title}
                      </span>
                      <span className="block text-xs text-foreground-600 mt-0.5 leading-relaxed">
                        {channel.desc}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-5 md:p-6 rounded-lg bg-primary-950 text-background-100">
              <h2 className="font-heading text-base font-semibold text-background-50">
                隐私承诺
              </h2>
              <p className="mt-2 text-sm text-background-200/90 leading-relaxed">
                投稿人身份默认脱敏，后台仅保留线索内容与可选的联系邮箱。私信记录不会公开，
                你也可以随时申请撤回稿件。疑问可发送至 {siteInfo.email}。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}